import {Component, inject, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import {CommonModule} from "@angular/common";
import {MatSelectModule} from "@angular/material/select";
import {MatFormFieldModule} from "@angular/material/form-field";
import {MatButtonModule} from "@angular/material/button";
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {KnowledgeBaseService} from "../data-access/knowledge-base.service";
import {MoveData, TreeNode} from "../kd-workplace/kd-workplace.component";
import {Category} from "../../../shared/interfaces/category";
import {Malfunction} from "../../../shared/interfaces/malfunction";
import {forkJoin, map, Observable} from "rxjs";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

interface MoveItemDialogData {
  node: TreeNode;
  expandedNodes: {
    productionLine: TreeNode | null;
    workplace: TreeNode | null;
    category: TreeNode | null;
    malfunction: TreeNode | null;
    solution: TreeNode | null;
  };
}

@Component({
  selector: 'app-move-item-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatSelectModule,
    MatFormFieldModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatProgressSpinner
  ],
  templateUrl: './move-item-dialog.component.html',
  styleUrl: './move-item-dialog.component.scss'
})
export class MoveItemDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  private knowledgeBaseService = inject(KnowledgeBaseService);
  private dialogRef = inject(MatDialogRef<MoveItemDialogComponent>);

  data: MoveItemDialogData = inject(MAT_DIALOG_DATA);

  moveForm!: FormGroup;
  categories = signal<Category[]>([]);
  malfunctions = signal<Malfunction[]>([]);
  loading = signal(true);

  ngOnInit() {
    this.initializeForm();
    this.loadFormData();
  }

  private initializeForm() {
    const node = this.data.node;
    const expandedNodes = this.data.expandedNodes;

    if (node.type === 'malfunction') {
      this.moveForm = this.fb.group({
        categoryId: [expandedNodes.category?.id || '', Validators.required]
      });
    } else if (node.type === 'solution') {
      this.moveForm = this.fb.group({
        categoryId: [expandedNodes.category?.id || '', Validators.required],
        malfunctionId: [expandedNodes.malfunction?.id || '', Validators.required]
      });

      // Listen to category changes to update malfunctions
      this.moveForm.get('categoryId')?.valueChanges.subscribe(categoryId => {
        if (categoryId) {
          this.loadMalfunctionsForCategory(categoryId);
          this.moveForm.get('malfunctionId')?.setValue('');
        }
      });
    }
  }

  private loadFormData() {
    const workplaceId = this.data.expandedNodes.workplace?.id;

    if (!workplaceId) {
      console.error('No workplace ID found');
      this.loading.set(false);
      return;
    }

    // Load categories for the current workplace
    this.knowledgeBaseService.getCategoriesForWorkplace(workplaceId).subscribe({
      next: (categories) => {
        this.categories.set(categories);

        // If moving a solution, also load malfunctions for the current category
        if (this.data.node.type === 'solution' && this.data.expandedNodes.category?.id) {
          this.loadMalfunctionsForCategory(this.data.expandedNodes.category.id);
        } else {
          this.loading.set(false);
        }
      },
      error: (error) => {
        console.error('Error loading categories:', error);
        this.loading.set(false);
      }
    });
  }

  private loadMalfunctionsForCategory(categoryId: string) {
    this.knowledgeBaseService.getMalfunctionsForCategory(categoryId).subscribe({
      next: (malfunctions) => {
        this.malfunctions.set(malfunctions);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading malfunctions:', error);
        this.loading.set(false);
      }
    });
  }

  getDialogTitle(): string {
    const node = this.data.node;
    switch (node.type) {
      case 'malfunction':
        return `Störung "${node.name}" verschieben`;
      case 'solution':
        return `Lösung "${node.name}" verschieben`;
      default:
        return 'Element verschieben';
    }
  }

  showCategoryDropdown(): boolean {
    return this.data.node.type === 'malfunction' || this.data.node.type === 'solution';
  }

  showMalfunctionDropdown(): boolean {
    return this.data.node.type === 'solution';
  }

  onCancel() {
    this.dialogRef.close();
  }

  onMove() {
    if (this.moveForm.valid) {
      const formValue = this.moveForm.value;

      const moveData: MoveData = {
        nodeId: this.data.node.id,
        nodeType: this.data.node.type,
        newParentIds: this.data.node.type === 'malfunction'
          ? {categoryId: formValue.categoryId}
          : {categoryId: formValue.categoryId, malfunctionId: formValue.malfunctionId}
      };

      // update node in database
      this.dialogRef.close(moveData);
    }
  }
}
